/**
 * F45 (Wave 2D) — retention cohorts page.
 *
 * Route: `/analytics/retention`. Permission: `analytics:read`.
 *
 * D1/D7/D30 retained-fraction curves by signup-week cohort, rendered as a Line
 * chart + a cohort Table. DateRangePicker bounds the cohort window.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useI18n } from '@bravemove/i18n/react';
import {
  Banner,
  Chart,
  DateRangePicker,
  Skeleton,
  Table,
  type DateRange,
  type TableColumn,
} from '@bravemove/ui-web';
import type { AnalyticsRetention, AnalyticsRetentionCohort } from '@bravemove/types';
import { adminAnalyticsApi } from '../../api/analytics';
import { PageHeader } from '../../components/PageHeader';
import { AnalyticsNav } from './AnalyticsNav';
import { defaultAnalyticsRange, type AnalyticsRangeValue } from './analyticsHelpers';

function pct(v: number): string {
  return `${Math.round(v * 1000) / 10}%`;
}

export function AnalyticsRetentionPage() {
  const { t, locale } = useI18n();

  const [range, setRange] = useState<AnalyticsRangeValue>(() => defaultAnalyticsRange());
  const [retention, setRetention] = useState<AnalyticsRetention | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchRetention = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setRetention(await adminAnalyticsApi.getRetention({ from: range.from, to: range.to }));
    } catch {
      setError(t('admin:analytics.error'));
    } finally {
      setLoading(false);
    }
  }, [range.from, range.to, t]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void fetchRetention();
  }, [fetchRetention]);

  const columns = useMemo<readonly TableColumn<AnalyticsRetentionCohort>[]>(
    () => [
      {
        key: 'cohortWeek',
        header: t('admin:analytics.retention.cohortWeek'),
        cell: (r) => r.cohortWeek,
      },
      {
        key: 'size',
        header: t('admin:analytics.retention.size'),
        align: 'end',
        cell: (r) => <span className="font-mono tabular-nums">{r.size.toLocaleString()}</span>,
      },
      {
        key: 'd1',
        header: t('admin:analytics.retention.d1'),
        align: 'end',
        cell: (r) => <span className="font-mono tabular-nums">{pct(r.d1)}</span>,
      },
      {
        key: 'd7',
        header: t('admin:analytics.retention.d7'),
        align: 'end',
        cell: (r) => <span className="font-mono tabular-nums">{pct(r.d7)}</span>,
      },
      {
        key: 'd30',
        header: t('admin:analytics.retention.d30'),
        align: 'end',
        cell: (r) => <span className="font-mono tabular-nums">{pct(r.d30)}</span>,
      },
    ],
    [t],
  );

  const onCustomRange = (dr: DateRange) =>
    setRange((prev) => ({ from: dr.start ?? prev.from, to: dr.end ?? prev.to }));

  return (
    <div className="space-y-6" data-testid="analytics-retention-page">
      <PageHeader eyebrow="Analytics" title={t('admin:analytics.retention.title')} />
      <AnalyticsNav />

      <DateRangePicker
        value={{ start: range.from, end: range.to }}
        onChange={onCustomRange}
        locale={locale}
      />

      {error ? <Banner tone="danger">{error}</Banner> : null}

      {loading || !retention ? (
        <Skeleton className="h-72 w-full rounded-xl" data-testid="analytics-retention-loading" />
      ) : (
        <>
          <section className="rounded-xl border border-hairline bg-surface p-4">
            <Chart
              kind="line"
              data={retention.cohorts.map((c) => ({
                cohort: c.cohortWeek,
                d1: Math.round(c.d1 * 1000) / 10,
                d7: Math.round(c.d7 * 1000) / 10,
                d30: Math.round(c.d30 * 1000) / 10,
              }))}
              xKey="cohort"
              showLegend
              series={[
                { key: 'd1', label: t('admin:analytics.retention.d1') },
                { key: 'd7', label: t('admin:analytics.retention.d7') },
                { key: 'd30', label: t('admin:analytics.retention.d30') },
              ]}
              height={280}
            />
          </section>
          <Table
            data={retention.cohorts as AnalyticsRetentionCohort[]}
            columns={columns as TableColumn<AnalyticsRetentionCohort>[]}
            rowKey={(r) => r.cohortWeek}
            density="compact"
          />
        </>
      )}
    </div>
  );
}

export default AnalyticsRetentionPage;
